import type { JsonApiDoc, MatcherFixture } from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';

/**
 * Fixtures for the "mid-journey borrower" PSLF demo: ~mid-way through the
 * 120 qualifying payments, three federal loans (NSLDS-linked), one submitted
 * PSLF form, and an active recommendation. Drives the main-app PSLF dashboard
 * (routes/authenticated/pslf{,/dashboard}) entirely through the demo handler.
 *
 * IDs: employee/person ids are placeholders — the demo handler rewrites them
 * (and any sideloaded back-references) to the live session ids the route asks
 * for. company / pslf-profile / pslf-form / etc. use stable demo ids that the
 * fixtures cross-reference, so the materialized records form one graph.
 *
 * Borrower/employer names derive from the {@link DemoFixtureIdentity} the
 * registry resolves from the active selection — the handler's id-rewrite means
 * these resources can overwrite the session identity records, so hardcoded
 * names here would rename the demo user/employer mid-session.
 */

const FEIN = '12-3456789';

const demoEmail = (identity: DemoFixtureIdentity) =>
  `${identity.firstName}.${identity.lastName}`.toLowerCase().replace(/\s+/g, '-') + '@example.com';

// (A) getLoanSummaryRequest builder — plain LoanSummary object (not JSON:API),
// returned as `.content`. Balances are dollars.
const LOAN_SUMMARY = {
  summaryByLoanType: [
    {
      loanType: 'direct',
      loanIds: ['demo-loan-1', 'demo-loan-2'],
      currentBalance: 45000,
      interestRate: 5.5,
      monthlyPayment: 350,
      repaymentPlan: 'ibr',
      repaymentPlans: ['ibr'],
      totalDisbursedAmount: 50000,
      federalLoanTime: '120',
    },
  ],
  errors: [],
};

// (B) employee findRecord — sideloads pslf-profile (so no createRecord fires),
// company (route reads employee.company.id), employee-addition, pslf-forms.
function buildEmployeeDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { firstName, lastName, employerName } = identity;
  return {
    data: {
      type: 'employee',
      id: 'demo-employee-1',
      attributes: {
        firstName,
        lastName,
        email: demoEmail(identity),
        isActive: true,
        title: 'Registered Nurse',
        employmentStartDate: '2022-01-15',
        customAttributes: { fein: FEIN },
      },
      relationships: {
        pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        company: { data: { type: 'company', id: 'demo-company-1' } },
        employeeAddition: { data: { type: 'employee-addition', id: 'demo-emp-add-1' } },
        pslfForms: { data: [{ type: 'pslf-form', id: 'demo-form-1' }] },
      },
    },
    included: [
      {
        type: 'pslf-profile',
        id: 'demo-pslf-profile-1',
        attributes: { isInterested: true, signaturePresent: true },
        relationships: {
          employee: { data: { type: 'employee', id: 'demo-employee-1' } },
          pslfForms: { data: [{ type: 'pslf-form', id: 'demo-form-1' }] },
        },
      },
      {
        type: 'company',
        id: 'demo-company-1',
        attributes: {
          name: employerName,
          companyType: 'partner',
          isActive: true,
          programTitle: 'PSLF Program',
        },
        relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
      },
      {
        type: 'employee-addition',
        id: 'demo-emp-add-1',
        attributes: { processed: { merged: { fein: FEIN } } },
      },
      {
        type: 'pslf-form',
        id: 'demo-form-1',
        attributes: { status: 'submitted', formType: 'currentEmployer' },
        relationships: {
          pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        },
      },
    ],
  };
}

// (C) company findRecord — sideloads pslf-groups (route returns company.pslfGroups).
function buildCompanyDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { employerName } = identity;
  return {
    data: {
      type: 'company',
      id: 'demo-company-1',
      attributes: {
        name: employerName,
        companyType: 'partner',
        isActive: true,
        programTitle: 'PSLF Program',
      },
      relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
    },
    included: [
      {
        type: 'pslf-group',
        id: 'demo-pslf-group-1',
        attributes: {
          approverEmail: 'hr@example.com',
          approverName: 'Sarah Johnson',
          approverTitle: 'HR Director',
          employerCommonName: employerName,
          employerLegalName: `${employerName} Inc.`,
          employerFein: FEIN,
          employerIsNotForProfit: true,
          employerCity: 'Portland',
          employerState: 'OR',
          employerPostalCode: '97205',
        },
        relationships: { company: { data: { type: 'company', id: 'demo-company-1' } } },
      },
    ],
  };
}

// (D) person findRecord — sideloads accounts -> loans + financial-institution.
// NSLDS provider + per-loan forgiveness-program-qualified drives hasNsldsPslfData.
function buildPersonDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { firstName, lastName } = identity;
  return {
    data: {
      type: 'person',
      id: 'demo-person-1',
      attributes: { firstName, lastName, preferences: {} },
      relationships: { accounts: { data: [{ type: 'account', id: 'demo-account-1' }] } },
    },
    included: [
      {
        type: 'account',
        id: 'demo-account-1',
        attributes: {
          isActive: true,
          publicOrdinalValue: 1,
          observation: {
            provider: 'ObservableProvider.NSLDS',
            balance: 45000,
            'interest-weighted-average': 5.5,
            'principal-balance': 45000,
            'interest-balance': 0,
            'is-paid-off': false,
          },
        },
        relationships: {
          person: { data: { type: 'person', id: 'demo-person-1' } },
          financialInstitution: { data: { type: 'financial-institution', id: 'demo-fi-1' } },
          loans: {
            data: [
              { type: 'loan', id: 'demo-loan-1' },
              { type: 'loan', id: 'demo-loan-2' },
            ],
          },
        },
      },
      {
        type: 'financial-institution',
        id: 'demo-fi-1',
        attributes: { name: 'MOHELA', isActive: true, isPublic: true },
      },
      {
        type: 'loan',
        id: 'demo-loan-1',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': 'Direct Unsubsidized',
            'loan-type': 'direct',
            'raw-loan-type': 'Direct Unsubsidized',
            balance: 30000,
            'principal-balance': 30000,
            'interest-rate': 5.5,
            'repayment-plan-derived': 'ibr',
            'forgiveness-program-qualified': 48,
            'is-paid-off': false,
            status: 'active',
          },
        },
        relationships: { account: { data: { type: 'account', id: 'demo-account-1' } } },
      },
      {
        type: 'loan',
        id: 'demo-loan-2',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': 'Direct Subsidized',
            'loan-type': 'direct',
            'raw-loan-type': 'Direct Subsidized',
            balance: 15000,
            'principal-balance': 15000,
            'interest-rate': 4.5,
            'repayment-plan-derived': 'ibr',
            'forgiveness-program-qualified': 48,
            'is-paid-off': false,
            status: 'active',
          },
        },
        relationships: { account: { data: { type: 'account', id: 'demo-account-1' } } },
      },
    ],
  };
}

// (E) pslf-form query — at least one submitted current-employer form so the
// dashboard renders the rich (forms) state.
function buildPslfFormCollection(identity: DemoFixtureIdentity): JsonApiDoc {
  const { firstName, lastName, employerName } = identity;
  return {
    data: [
      {
        type: 'pslf-form',
        id: 'demo-form-1',
        attributes: {
          status: 'submitted',
          formType: 'currentEmployer',
          borrowerCompletedAt: '2026-05-17',
          employerCompletedAt: '2026-05-21',
          submittedAt: '2026-05-22',
          isNonPartner: false,
          borrowerFields: {
            'borrower-name': `${firstName} ${lastName}`,
            'borrower-email': demoEmail(identity),
            'borrower-employer-name': employerName,
            'borrower-employment-start-date': '2022-01-15',
            'borrower-employment-status': 'employed',
            'borrower-employment-continues': true,
            'borrower-employer-ein': FEIN,
          },
          employerFields: {
            'employer-name': employerName,
            'employer-fein': FEIN,
            'employer-city': 'Portland',
            'employer-state': 'OR',
            'employer-zip': '97205',
            'employer-email': 'hr@example.com',
          },
        },
        relationships: {
          pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
          attachments: { data: [] },
        },
      },
    ],
  };
}

// (F) recommendation query — drives the recommendation card. Plan summaries are
// arrays of cents (model getters divide by 100). User relationship omitted (the
// dashboard does not dereference it) to avoid coupling to the live user id.
const RECOMMENDATION_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'recommendation',
      id: 'demo-recommendation-1',
      attributes: {
        hasErrorInCalcs: false,
        requestingApp: 'PSLF',
        bestPlanSummary: {
          'payoff-amount': [2520000],
          'term-months': [72],
          'monthly-payments': [35000],
          'total-interest-paid': [480000],
          'estimated-amount-forgiven': [1800000],
        },
        currentPlanSummary: {
          'payoff-amount': [6000000],
          'term-months': [120],
          'monthly-payments': [55000],
          'total-interest-paid': [1500000],
          'estimated-amount-forgiven': [0],
        },
        bestPlanNextSteps: [],
        bestPlanTableHeadings: [],
        currentPlanTableHeadings: [],
        inputSummary: {},
        outputSummary: {},
      },
    },
  ],
};

// (G) answer-profile query — returned non-empty so the dashboard never triggers
// createRecord. Null *At fields keep the income/repayment recommendations live.
const ANSWER_PROFILE_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'answer-profile',
      id: 'demo-answer-profile-1',
      attributes: {
        consolidatedLoansAt: null,
        changedRepaymentPlanAt: null,
        recertifiedIncomeAt: null,
      },
    },
  ],
};

export function buildMidJourneyPslfMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    { matcher: { kind: 'builder', urlIncludes: 'summarize-loans' }, content: LOAN_SUMMARY },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
      docs: [buildEmployeeDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'company' },
      docs: [buildCompanyDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'person' },
      docs: [buildPersonDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'pslf-form' },
      docs: [buildPslfFormCollection(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'recommendation' },
      docs: [RECOMMENDATION_COLLECTION],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'answer-profile' },
      docs: [ANSWER_PROFILE_COLLECTION],
    },
  ];
}
